import {
  collection,
  getCountFromServer,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  where,
  type DocumentData,
  type QueryDocumentSnapshot,
  type QueryConstraint,
} from 'firebase/firestore';
import { firestore } from '../firebase';
import { ServiceProvider, ServiceVerificationStatus, type BusinessHours } from '../types/firestore';
import {
  FULFILLMENT_MODES,
  getFulfillmentModeLabel,
  resolveServiceFulfillmentCapabilities,
} from '../shared/fulfillment';
import {
  getProviderCompleteness,
  getProviderRiskFlags,
  isOperationalAdminProvider,
  isRealAdminProvider,
  isTestAdminProvider,
  isVisibleAdminProvider,
  type AdminProviderRiskFlag,
} from './adminCommandCenterService';
import { resolveServiceVerificationStatus } from '../utils/serviceVerification';
import { getServiceProviderProfilePath } from '../utils/serviceProviderProfilePath';

export const ADMIN_SERVICE_PROVIDER_PAGE_SIZE = 120;
export const ADMIN_SERVICE_PROVIDER_RISK_SCAN_LIMIT = 300;

export type AdminServiceProviderCursor = QueryDocumentSnapshot<DocumentData> | null;
export type AdminProviderDirectoryStatus = ServiceVerificationStatus | 'rejected' | 'unknown';

export interface AdminServiceProvidersMetric {
  value: number | null;
  label: string;
  description: string;
  partial?: boolean;
}

export interface AdminServiceProvidersMetrics {
  total: AdminServiceProvidersMetric;
  real: AdminServiceProvidersMetric;
  test: AdminServiceProvidersMetric;
  operational: AdminServiceProvidersMetric;
  pending: AdminServiceProvidersMetric;
  withMissingData: AdminServiceProvidersMetric;
}

export interface AdminServiceProviderDirectoryItem {
  uid: string;
  serviceName: string;
  city: string | null;
  addressSummary: string | null;
  ownerEmail: string | null;
  phoneNumber: string | null;
  verificationStatus: AdminProviderDirectoryStatus;
  verificationStatusLabel: string;
  isVerified: boolean;
  isTestData: boolean;
  isReal: boolean;
  isVisible: boolean;
  isOperational: boolean;
  completeness: ReturnType<typeof getProviderCompleteness>;
  completenessScore: number;
  completenessTotal: number;
  completenessLabel: string;
  riskFlags: AdminProviderRiskFlag[];
  riskLabels: string[];
  createdAt: Date | null;
  updatedAt: Date | null;
  createdAtLabel: string;
  updatedAtLabel: string;
  specializations: string[];
  fulfillmentModeLabels: string[];
  businessHoursSummary: string;
  locationSummary: string;
  imageStatus: string;
  publicProfilePath: string | null;
  verificationPath: string | null;
  toolsPath: string | null;
  searchText: string;
  rawProvider: Partial<ServiceProvider>;
}

export interface AdminServiceProvidersReadModel {
  metrics: AdminServiceProvidersMetrics;
  providers: AdminServiceProviderDirectoryItem[];
  cursor: AdminServiceProviderCursor;
  hasMore: boolean;
  isPartial: boolean;
  partialError: string | null;
  readAt: Date;
  readLimits: {
    pageSize: number;
    riskScanLimit: number;
  };
}

const metric = (
  value: number | null,
  label: string,
  description: string,
  partial = false
): AdminServiceProvidersMetric => ({
  value,
  label,
  description,
  partial,
});

const emptyMetrics = (): AdminServiceProvidersMetrics => ({
  total: metric(null, 'Wszystkie serwisy', 'Liczba dokumentów serviceProviders', true),
  real: metric(null, 'Realne', 'serviceProviders.isTestData !== true', true),
  test: metric(null, 'Testowe', 'serviceProviders.isTestData == true', true),
  operational: metric(null, 'Operacyjne', 'approved + isVerified + non-test', true),
  pending: metric(null, 'Pending', "verificationStatus == 'pending_approval'", true),
  withMissingData: metric(
    null,
    'Z brakami danych',
    'Braki profilu w ograniczonym skanie serviceProviders',
    true
  ),
});

const countCollection = async (
  collectionName: string,
  ...constraints: QueryConstraint[]
): Promise<number> => {
  const snapshot = await getCountFromServer(
    query(collection(firestore, collectionName), ...constraints)
  );
  return snapshot.data().count;
};

const normalizeTimestamp = (value: unknown): Date | null => {
  if (!value) return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
  if (typeof (value as { toDate?: unknown }).toDate === 'function') {
    const date = (value as { toDate: () => Date }).toDate();
    return Number.isNaN(date.getTime()) ? null : date;
  }
  if (
    typeof (value as { seconds?: unknown }).seconds === 'number' &&
    Number.isFinite((value as { seconds: number }).seconds)
  ) {
    return new Date((value as { seconds: number }).seconds * 1000);
  }
  return null;
};

const formatDate = (date: Date | null): string =>
  date
    ? new Intl.DateTimeFormat('pl-PL', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      }).format(date)
    : 'Brak daty';

const trimString = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed || null;
};

const normalizeStatus = (
  provider: Partial<ServiceProvider> & Record<string, unknown>
): AdminProviderDirectoryStatus => {
  const rawStatus = trimString(provider.verificationStatus);
  if (rawStatus) {
    const normalized = rawStatus.toLowerCase();
    if (
      normalized === 'profile_incomplete' ||
      normalized === 'incomplete' ||
      normalized === 'draft'
    ) {
      return 'profile_incomplete';
    }
    if (normalized === 'pending_approval' || normalized === 'pending') {
      return 'pending_approval';
    }
    if (normalized === 'approved' || normalized === 'active' || normalized === 'verified') {
      return 'approved';
    }
    if (normalized === 'changes_requested' || normalized === 'requires_changes') {
      return 'changes_requested';
    }
    if (normalized === 'rejected') return 'rejected';
    return 'unknown';
  }
  const resolved = resolveServiceVerificationStatus(provider);
  return resolved || 'unknown';
};

export const getAdminProviderStatusLabel = (status: AdminProviderDirectoryStatus): string => {
  const labels: Record<AdminProviderDirectoryStatus, string> = {
    approved: 'Approved',
    pending_approval: 'Pending',
    changes_requested: 'Do poprawek',
    profile_incomplete: 'Niekompletny',
    rejected: 'Odrzucony',
    unknown: 'Nieznany status',
  };
  return labels[status] || 'Nieznany status';
};

export const getAdminProviderRiskLabel = (flag: AdminProviderRiskFlag): string => {
  const labels: Record<AdminProviderRiskFlag, string> = {
    missingLocation: 'Brak lokalizacji',
    missingProfileImage: 'Brak zdjęcia',
    missingBusinessHours: 'Brak godzin pracy',
    missingServiceFulfillment: 'Brak metod realizacji',
    missingSpecializations: 'Brak specjalizacji',
  };
  return labels[flag];
};

const buildAddressSummary = (provider: Partial<ServiceProvider>): string | null => {
  const cityLine = [trimString(provider.address?.postalCode), trimString(provider.address?.city)]
    .filter(Boolean)
    .join(' ');
  return [trimString(provider.address?.street), cityLine].filter(Boolean).join(', ') || null;
};

const buildLocationSummary = (provider: Partial<ServiceProvider>): string => {
  const location = provider.location as Record<string, unknown> | null | undefined;
  if (!location || typeof location !== 'object') return 'Brak poprawnych współrzędnych';
  const latitude = [location.latitude, location.lat, location._latitude].find(
    (item) => typeof item === 'number' && Number.isFinite(item)
  ) as number | undefined;
  const longitude = [location.longitude, location.lng, location.lon, location._longitude].find(
    (item) => typeof item === 'number' && Number.isFinite(item)
  ) as number | undefined;
  if (latitude === undefined || longitude === undefined) {
    return 'Brak poprawnych współrzędnych';
  }
  return `${latitude.toFixed(5)}, ${longitude.toFixed(5)}`;
};

const summarizeBusinessHours = (businessHours?: BusinessHours | null): string => {
  if (!businessHours?.weekly) return 'Brak godzin pracy';
  const enabledDays = Object.values(businessHours.weekly).filter((day) => day?.enabled === true);
  if (enabledDays.length === 0) return 'Godziny zapisane, bez aktywnych dni';
  return `${enabledDays.length} dni aktywnych`;
};

const getFulfillmentModeLabels = (provider: Partial<ServiceProvider>): string[] => {
  if (!provider.serviceFulfillment || typeof provider.serviceFulfillment !== 'object') return [];
  const capabilities = resolveServiceFulfillmentCapabilities(provider);
  return FULFILLMENT_MODES.filter((mode) => capabilities.modes[mode].enabled).map((mode) =>
    getFulfillmentModeLabel(mode)
  );
};

const calculateCompleteness = (provider: Partial<ServiceProvider>) => {
  const completeness = getProviderCompleteness(provider);
  const values = Object.values(completeness);
  const score = values.filter(Boolean).length;
  return {
    completeness,
    score,
    total: values.length,
    label: `${score}/${values.length}`,
  };
};

export const providerDocToDirectoryItem = (
  uid: string,
  data: Record<string, unknown>
): AdminServiceProviderDirectoryItem => {
  const provider = { uid, ...data } as Partial<ServiceProvider> & Record<string, unknown>;
  const serviceName = trimString(provider.serviceName) || 'Brak nazwy serwisu';
  const city = trimString(provider.address?.city);
  const ownerEmail = trimString(provider.email);
  const status = normalizeStatus(provider);
  const riskFlags = getProviderRiskFlags(provider);
  const riskLabels = riskFlags.map(getAdminProviderRiskLabel);
  const completeness = calculateCompleteness(provider);
  const createdAt = normalizeTimestamp(provider.createdAt);
  const updatedAt = normalizeTimestamp(provider.updatedAt);
  const specializations = Array.isArray(provider.specializations)
    ? provider.specializations
        .map((item) => trimString(item))
        .filter((item): item is string => Boolean(item))
    : [];
  const fulfillmentModeLabels = getFulfillmentModeLabels(provider);
  const isVisible = isVisibleAdminProvider(provider);
  const isTestData = isTestAdminProvider(provider);

  return {
    uid,
    serviceName,
    city,
    addressSummary: buildAddressSummary(provider),
    ownerEmail,
    phoneNumber: trimString(provider.phoneNumber),
    verificationStatus: status,
    verificationStatusLabel: getAdminProviderStatusLabel(status),
    isVerified: provider.isVerified === true,
    isTestData,
    isReal: isRealAdminProvider(provider),
    isVisible,
    isOperational: isOperationalAdminProvider(provider),
    completeness: completeness.completeness,
    completenessScore: completeness.score,
    completenessTotal: completeness.total,
    completenessLabel: completeness.label,
    riskFlags,
    riskLabels,
    createdAt,
    updatedAt,
    createdAtLabel: formatDate(createdAt),
    updatedAtLabel: formatDate(updatedAt),
    specializations,
    fulfillmentModeLabels,
    businessHoursSummary: summarizeBusinessHours(provider.businessHours),
    locationSummary: buildLocationSummary(provider),
    imageStatus: provider.profileImage
      ? 'Canonical profileImage zapisane'
      : 'Brak canonical zdjęcia',
    publicProfilePath: isVisible ? getServiceProviderProfilePath(uid) : null,
    verificationPath: status === 'pending_approval' ? '/admin/verifications' : null,
    toolsPath: isTestData ? '/admin/tools' : null,
    searchText: [serviceName, city, ownerEmail, provider.phoneNumber, buildAddressSummary(provider)]
      .filter(Boolean)
      .join(' ')
      .toLowerCase(),
    rawProvider: provider,
  };
};

const loadProviderMetrics = async (): Promise<AdminServiceProvidersMetrics> => {
  const [total, test, visible, visibleTest, pending, riskScanSnapshot] = await Promise.all([
    countCollection('serviceProviders'),
    countCollection('serviceProviders', where('isTestData', '==', true)),
    countCollection(
      'serviceProviders',
      where('verificationStatus', '==', 'approved'),
      where('isVerified', '==', true)
    ),
    countCollection(
      'serviceProviders',
      where('verificationStatus', '==', 'approved'),
      where('isVerified', '==', true),
      where('isTestData', '==', true)
    ),
    countCollection('serviceProviders', where('verificationStatus', '==', 'pending_approval')),
    getDocs(
      query(
        collection(firestore, 'serviceProviders'),
        orderBy('updatedAt', 'desc'),
        limit(ADMIN_SERVICE_PROVIDER_RISK_SCAN_LIMIT)
      )
    ),
  ]);

  const providersWithMissingData = riskScanSnapshot.docs
    .map((docSnap) => ({ uid: docSnap.id, ...(docSnap.data() as Partial<ServiceProvider>) }))
    .filter((provider) => getProviderRiskFlags(provider).length > 0).length;
  const riskScanPartial = riskScanSnapshot.size >= ADMIN_SERVICE_PROVIDER_RISK_SCAN_LIMIT;

  return {
    total: metric(total, 'Wszystkie serwisy', 'Liczba dokumentów serviceProviders'),
    real: metric(Math.max(total - test, 0), 'Realne', 'serviceProviders.isTestData !== true'),
    test: metric(test, 'Testowe', 'serviceProviders.isTestData == true'),
    operational: metric(
      Math.max(visible - visibleTest, 0),
      'Operacyjne',
      'approved + isVerified + non-test'
    ),
    pending: metric(pending, 'Pending', "verificationStatus == 'pending_approval'"),
    withMissingData: metric(
      providersWithMissingData,
      'Z brakami danych',
      `Braki profilu w limicie ${ADMIN_SERVICE_PROVIDER_RISK_SCAN_LIMIT}`,
      riskScanPartial
    ),
  };
};

const loadProviderPage = async (
  pageSize: number,
  cursor: AdminServiceProviderCursor
): Promise<{
  providers: AdminServiceProviderDirectoryItem[];
  cursor: AdminServiceProviderCursor;
  hasMore: boolean;
}> => {
  const constraints: QueryConstraint[] = [orderBy('updatedAt', 'desc'), limit(pageSize)];
  if (cursor) {
    constraints.splice(1, 0, startAfter(cursor));
  }
  const snapshot = await getDocs(query(collection(firestore, 'serviceProviders'), ...constraints));
  const providers = snapshot.docs.map((docSnap) =>
    providerDocToDirectoryItem(docSnap.id, docSnap.data())
  );

  return {
    providers,
    cursor: snapshot.docs[snapshot.docs.length - 1] || null,
    hasMore: snapshot.size >= pageSize,
  };
};

export const getAdminServiceProvidersReadModel = async (
  pageSize = ADMIN_SERVICE_PROVIDER_PAGE_SIZE,
  cursor: AdminServiceProviderCursor = null
): Promise<AdminServiceProvidersReadModel> => {
  const [metricsResult, pageResult] = await Promise.allSettled([
    loadProviderMetrics(),
    loadProviderPage(pageSize, cursor),
  ]);

  if (pageResult.status === 'rejected') {
    throw pageResult.reason;
  }

  const metrics = metricsResult.status === 'fulfilled' ? metricsResult.value : emptyMetrics();
  const partialError =
    metricsResult.status === 'rejected'
      ? 'Nie udało się odczytać części metryk. Lista serwisów jest dostępna.'
      : null;

  return {
    metrics,
    providers: pageResult.value.providers,
    cursor: pageResult.value.cursor,
    hasMore: pageResult.value.hasMore,
    isPartial: Boolean(partialError) || Object.values(metrics).some((entry) => entry.partial),
    partialError,
    readAt: new Date(),
    readLimits: {
      pageSize,
      riskScanLimit: ADMIN_SERVICE_PROVIDER_RISK_SCAN_LIMIT,
    },
  };
};
